import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const LEVEL_CFG = {
  debug: { color: '#6B6B8A', bg: '#6B6B8A18', label: 'DEBUG',    icon: '🔍' },
  info:  { color: '#60A5FA', bg: '#60A5FA18', label: 'INFO',     icon: 'ℹ️'  },
  warn:  { color: '#FBBF24', bg: '#FBBF2418', label: 'ADVARSEL', icon: '⚠️'  },
  error: { color: '#F87171', bg: '#F8717118', label: 'FEIL',     icon: '❌'  },
  fatal: { color: '#F7605C', bg: '#F7605C28', label: 'KRITISK',  icon: '🚨'  },
};

const CAT_ICON = {
  auth:'🔐', api:'🌐', database:'🗄️', email:'📧', calendar:'📅', chat:'💬',
  shopping:'🛒', budget:'💰', medication:'💊', contacts:'👥', photos:'📸',
  google:'🔵', facebook:'🔵', push:'🔔', system:'⚙️', security:'🛡️', imap:'📬',
};

const CATEGORIES = Object.keys(CAT_ICON);

export default function LogViewer() {
  const [logs,       setLogs]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [selected,   setSelected]   = useState(null);
  const [stats,      setStats]      = useState(null);
  const [autoRefresh,setAutoRefresh]= useState(false);
  const [filters, setFilters] = useState({ level:'', category:'', search:'', from:'', to:'' });

  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(1); };

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50, ...Object.fromEntries(Object.entries(filters).filter(([,v]) => v)) });
      const res = await axios.get(`/api/logs?${params}`, AUTH());
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
      setStats(res.data.stats);
    } catch(e) {
      console.error('Logg-feil:', e.message);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { loadLogs(); }, [loadLogs]);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(loadLogs, 5000);
    return () => clearInterval(t);
  }, [autoRefresh, loadLogs]);

  const download = (format) => {
    const params = new URLSearchParams({ format, ...Object.fromEntries(Object.entries(filters).filter(([,v]) => v)) });
    window.open(`/api/logs/export/download?${params}`, '_blank');
  };

  return (
    <div>
      {/* Stats */}
      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:10, marginBottom:16 }}>
          {[
            { label:'Totalt (24t)', value:stats.total,        color:'var(--txt)'    },
            { label:'Feil',         value:stats.errors,       color:'var(--red)'    },
            { label:'Advarsler',    value:stats.warnings,     color:'#FBBF24'       },
            { label:'Info',         value:stats.infos,        color:'var(--accent)' },
            { label:'Brukere',      value:stats.unique_users, color:'#34D399'       },
          ].map((s, i) => (
            <div key={i} style={{ background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:12, padding:'12px 14px', textAlign:'center' }}>
              <div style={{ fontSize:24, fontWeight:800, color:s.color }}>{s.value ?? 0}</div>
              <div style={{ fontSize:10, color:'var(--txl)', fontWeight:600, textTransform:'uppercase', marginTop:2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter-rad */}
      <div style={{ background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:14, padding:14, marginBottom:12 }}>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <input style={inp} placeholder="🔍 Søk…" value={filters.search} onChange={e => setFilter('search', e.target.value)} />

          <select style={sel} value={filters.level} onChange={e => setFilter('level', e.target.value)}>
            <option value="">Alle nivåer</option>
            {Object.entries(LEVEL_CFG).map(([k,v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
          </select>

          <select style={sel} value={filters.category} onChange={e => setFilter('category', e.target.value)}>
            <option value="">Alle kategorier</option>
            {CATEGORIES.map(k => <option key={k} value={k}>{CAT_ICON[k]} {k}</option>)}
          </select>

          <input style={{...sel, fontSize:11}} type="datetime-local" value={filters.from} onChange={e => setFilter('from', e.target.value)} />
          <input style={{...sel, fontSize:11}} type="datetime-local" value={filters.to}   onChange={e => setFilter('to',   e.target.value)} />

          <button style={btn} onClick={() => { setFilters({ level:'', category:'', search:'', from:'', to:'' }); setPage(1); }}>✕ Nullstill</button>
          <button style={btn} onClick={loadLogs}>🔄</button>
          <button style={{ ...btn, background: autoRefresh ? 'rgba(52,211,153,.15)' : 'var(--card)', color: autoRefresh ? '#34D399' : 'var(--txm)', borderColor: autoRefresh ? '#34D399' : 'var(--bdr)' }}
            onClick={() => setAutoRefresh(v => !v)}>
            {autoRefresh ? '⏸ Pause' : '▶ Live'}
          </button>
          <button style={btn} onClick={() => download('json')} title="Last ned JSON">⬇️ JSON</button>
          <button style={btn} onClick={() => download('csv')}  title="Last ned CSV">⬇️ CSV</button>
        </div>
        <div style={{ marginTop:8, fontSize:11, color:'var(--txl)' }}>
          {loading ? 'Laster…' : `${total.toLocaleString()} logger · Side ${page} av ${totalPages}`}
          {autoRefresh && <span style={{ marginLeft:8, color:'#34D399', fontWeight:700 }}>● Live</span>}
        </div>
      </div>

      {/* Tabell */}
      <div style={{ background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:14, overflow:'hidden', marginBottom:12 }}>
        {/* Header */}
        <div style={{ display:'grid', gridTemplateColumns:'130px 80px 90px 1fr 110px 75px', gap:8, padding:'9px 14px', background:'var(--bg2)', borderBottom:'1px solid var(--bdr)', fontSize:10, fontWeight:700, color:'var(--txl)', textTransform:'uppercase', letterSpacing:'.5px' }}>
          <div>Tidspunkt</div><div>Nivå</div><div>Kategori</div><div>Melding</div><div>Bruker</div><div>Status</div>
        </div>

        {loading && (
          <div style={{ padding:30, textAlign:'center', color:'var(--txl)' }}>
            <div style={{ width:28, height:28, margin:'0 auto 10px', border:'3px solid var(--bdr)', borderTop:'3px solid var(--accent)', borderRadius:'50%', animation:'spin .8s linear infinite' }}/>
            Henter logger…
          </div>
        )}
        {!loading && logs.length === 0 && (
          <div style={{ padding:30, textAlign:'center', color:'var(--txl)', fontSize:14 }}>Ingen logger funnet</div>
        )}

        {!loading && logs.map((log, i) => {
          const lvl = LEVEL_CFG[log.level] || LEVEL_CFG.info;
          const isErr = log.level === 'error' || log.level === 'fatal';
          return (
            <div key={log.id} onClick={() => setSelected(log)}
              style={{ display:'grid', gridTemplateColumns:'130px 80px 90px 1fr 110px 75px', gap:8, padding:'9px 14px', borderBottom: i < logs.length-1 ? '1px solid var(--bdr)' : 'none', cursor:'pointer', background: isErr ? lvl.bg : 'transparent', transition:'background .1s' }}
              onMouseOver={e => e.currentTarget.style.background='var(--bg2)'}
              onMouseOut={e  => e.currentTarget.style.background= isErr ? lvl.bg : 'transparent'}
            >
              <div style={{ fontSize:10, color:'var(--txl)', fontFamily:'monospace' }}>
                {new Date(log.timestamp).toLocaleString('nb-NO',{ day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit' })}
              </div>
              <div>
                <span style={{ fontSize:9, fontWeight:700, padding:'2px 6px', borderRadius:20, background:lvl.bg, color:lvl.color }}>{lvl.icon} {lvl.label}</span>
              </div>
              <div style={{ fontSize:11, color:'var(--txm)', display:'flex', alignItems:'center', gap:3 }}>
                <span>{CAT_ICON[log.category] || '⚙️'}</span>
                <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{log.category}</span>
              </div>
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:12, color:'var(--txt)', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {log.action && <span style={{ fontSize:9, color:'var(--accent)', marginRight:5, fontFamily:'monospace' }}>{log.action}</span>}
                  {log.message}
                </div>
                {log.path && <div style={{ fontSize:9, color:'var(--txl)', fontFamily:'monospace' }}>{log.method} {log.path}</div>}
                {(log.details || log.stack_trace) && <span style={{ fontSize:9, color:'var(--accent)', fontWeight:700 }}>+ detaljer</span>}
              </div>
              <div style={{ fontSize:11, color:'var(--txm)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {log.user_name || log.user_email || '—'}
              </div>
              <div style={{ fontSize:11, fontFamily:'monospace', fontWeight:700, color: log.status_code >= 500 ? 'var(--red)' : log.status_code >= 400 ? '#FBBF24' : log.status_code >= 200 ? '#34D399' : 'var(--txl)' }}>
                {log.status_code || ''}{log.duration_ms ? ` ${log.duration_ms}ms` : ''}
              </div>
            </div>
          );
        })}
      </div>

      {/* Paginering */}
      {totalPages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:6, marginBottom:16 }}>
          <button style={{ ...btn, opacity: page > 1 ? 1 : 0.4 }} onClick={() => setPage(1)} disabled={page===1}>«</button>
          <button style={{ ...btn, opacity: page > 1 ? 1 : 0.4 }} onClick={() => setPage(p=>p-1)} disabled={page<=1}>‹</button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let p = page <= 3 ? i+1 : page >= totalPages-2 ? totalPages-4+i : page-2+i;
            p = Math.max(1, Math.min(totalPages, p));
            return p;
          }).filter((p, i, a) => a.indexOf(p) === i).map(p => (
            <button key={p} onClick={() => setPage(p)} style={{ ...btn, minWidth:34, background: page===p ? 'var(--accent)' : 'var(--card)', color: page===p ? 'white' : 'var(--txm)', borderColor: page===p ? 'var(--accent)' : 'var(--bdr)', fontWeight: page===p ? 700 : 500 }}>{p}</button>
          ))}
          <button style={{ ...btn, opacity: page < totalPages ? 1 : 0.4 }} onClick={() => setPage(p=>p+1)} disabled={page>=totalPages}>›</button>
          <button style={{ ...btn, opacity: page < totalPages ? 1 : 0.4 }} onClick={() => setPage(totalPages)} disabled={page===totalPages}>»</button>
          <span style={{ fontSize:11, color:'var(--txl)', marginLeft:4 }}>Side {page}/{totalPages} ({total.toLocaleString()})</span>
        </div>
      )}

      {/* Detalj-modal */}
      {selected && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20, backdropFilter:'blur(4px)' }}>
          <div style={{ background:'var(--card)', borderRadius:20, padding:24, width:'100%', maxWidth:680, maxHeight:'85vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.4)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:18 }}>
              <div>
                <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:6 }}>
                  {(() => { const l = LEVEL_CFG[selected.level] || LEVEL_CFG.info; return <span style={{ fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:20, background:l.bg, color:l.color }}>{l.icon} {l.label}</span>; })()}
                  <span style={{ fontSize:11, color:'var(--txl)' }}>{CAT_ICON[selected.category]} {selected.category}</span>
                </div>
                <div style={{ fontSize:15, fontWeight:800, color:'var(--txt)' }}>{selected.action}</div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background:'var(--bg2)', border:'none', borderRadius:'50%', width:30, height:30, cursor:'pointer', fontSize:14, color:'var(--txm)' }}>✕</button>
            </div>

            {/* Meta-grid */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:14 }}>
              {[
                ['Tidspunkt',  new Date(selected.timestamp).toLocaleString('nb-NO')],
                ['Request ID', selected.request_id || '—'],
                ['Bruker',     selected.user_name || selected.user_email || '—'],
                ['IP',         selected.ip_address || '—'],
                ['Metode',     selected.method || '—'],
                ['URL',        selected.path || '—'],
                ['Status',     selected.status_code || '—'],
                ['Varighet',   selected.duration_ms ? `${selected.duration_ms}ms` : '—'],
              ].map(([label, value]) => (
                <div key={label} style={{ background:'var(--bg2)', borderRadius:9, padding:'9px 11px' }}>
                  <div style={{ fontSize:9, fontWeight:700, color:'var(--txl)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:2 }}>{label}</div>
                  <div style={{ fontSize:12, color:'var(--txt)', fontFamily:'monospace', wordBreak:'break-all' }}>{value}</div>
                </div>
              ))}
            </div>

            <div style={{ background:'var(--bg2)', borderRadius:9, padding:'11px 13px', marginBottom:10 }}>
              <div style={{ fontSize:9, fontWeight:700, color:'var(--txl)', textTransform:'uppercase', marginBottom:5 }}>Melding</div>
              <div style={{ fontSize:13, color:'var(--txt)', lineHeight:1.6 }}>{selected.message}</div>
            </div>

            {selected.details && (
              <div style={{ background:'#0A0A14', borderRadius:9, padding:'11px 13px', marginBottom:10 }}>
                <div style={{ fontSize:9, fontWeight:700, color:'#6B6B8A', textTransform:'uppercase', marginBottom:5 }}>Detaljer</div>
                <pre style={{ fontSize:11, color:'#E8E8F8', margin:0, whiteSpace:'pre-wrap', wordBreak:'break-all', fontFamily:'monospace', lineHeight:1.6 }}>
                  {(() => { try { return JSON.stringify(JSON.parse(selected.details), null, 2); } catch { return selected.details; } })()}
                </pre>
              </div>
            )}

            {selected.stack_trace && (
              <div style={{ background:'#1A0A0A', border:'1px solid #F8717140', borderRadius:9, padding:'11px 13px', marginBottom:10 }}>
                <div style={{ fontSize:9, fontWeight:700, color:'#F87171', textTransform:'uppercase', marginBottom:5 }}>Stack trace</div>
                <pre style={{ fontSize:10, color:'#F8D4D4', margin:0, whiteSpace:'pre-wrap', wordBreak:'break-all', fontFamily:'monospace', lineHeight:1.5 }}>{selected.stack_trace}</pre>
              </div>
            )}

            {selected.user_agent && (
              <div style={{ fontSize:10, color:'var(--txl)', fontFamily:'monospace', padding:'7px 11px', background:'var(--bg2)', borderRadius:8 }}>{selected.user_agent}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const inp = { flex:1, minWidth:150, padding:'8px 11px', borderRadius:9, border:'1px solid var(--bdr)', background:'var(--bg2)', color:'var(--txt)', fontSize:12, fontFamily:'inherit', outline:'none' };
const sel = { padding:'8px 10px', borderRadius:9, border:'1px solid var(--bdr)', background:'var(--bg2)', color:'var(--txt)', fontSize:12, fontFamily:'inherit', outline:'none', cursor:'pointer' };
const btn = { padding:'8px 11px', borderRadius:9, border:'1px solid var(--bdr)', background:'var(--card)', color:'var(--txm)', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' };
